<script lang="ts" setup>
import type { IaLinkProps } from '@develop/ia-component-library'
import type { NuxtLinkProps } from '~/models/nuxt'

type IaLinkStyleProps = Omit<IaLinkProps, 'to' | 'href' | 'as' | 'download'>
type Props = NuxtLinkProps & IaLinkStyleProps & {
  custom?: boolean
  spanClass?: string
  linkClass?: string
}

const props = defineProps<Props & {
  ariaLabel?: string
}>()

const isExternalLink = (href: string | undefined) => {
  return href?.startsWith('http') || href?.startsWith('tel:')
}

const nuxtLinkProps = computed(() => {
  const { to, href, external, replace, custom, target, rel, noRel, prefetch, activeClass, exactActiveClass, prefetchedClass, ariaCurrentValue } = props

  const isExternal = external || isExternalLink(href)

  return {
    to: isExternal ? undefined : to, // Use `to` only for internal links
    href: isExternal ? href : undefined, // Use `href` for external links
    external: isExternal,
    replace,
    custom,
    target,
    rel,
    noRel,
    prefetch,
    activeClass,
    exactActiveClass,
    prefetchedClass,
    ariaCurrentValue,
  }
})

const isKeyOfIaLinkStyleProps = (key: keyof Props): key is keyof IaLinkStyleProps => {
  return !Object.prototype.hasOwnProperty.call(nuxtLinkProps.value, key)
}

const iaLinkProps = computed(() => {
  const linkProps = (Object.keys(props) as Array<keyof Props>)
    .filter(isKeyOfIaLinkStyleProps)
    .reduce((acc, key) => {
      if (props[key])
        acc[key] = props[key]
      return acc
    }, {} as Record<keyof IaLinkStyleProps, Props[keyof Props]>)
  return linkProps
})

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <NuxtLink
    v-if="!nuxtLinkProps.external"
    v-bind="nuxtLinkProps"
    :aria-label="ariaLabel"
    :class="linkClass"
  >
    <IaLink
      as="span"
      class="inline-block"
      :class="spanClass"
      v-bind="{ ...$attrs, ...iaLinkProps }"
    >
      <slot></slot>
    </IaLink>
  </NuxtLink>
  <IaLink
    v-else
    as="a"
    :class="[linkClass, spanClass]"
    v-bind="{ ...$attrs, ...nuxtLinkProps }"
    target="_blank"
  >
    <slot></slot>
  </IaLink>
</template>
